import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ConversationParticipantsListResponsive from './ConversationParticipantsListResponsive';
import { selectorsResponsive as conversationSelectors } from '../../../../../../store/conversations';
import { fetchUser } from '../../../../../../../core/actions/users/fetchUsers.action';
import { INBOX_REMOVE_PARTICIPANT_FROM_CONVERSATION } from '../../../../../../../core/conversations/actions';
import * as api from '../../../../../../../core/conversations/api/directMessages';

interface ConversationParticipantsListResponsiveContainerProps {
  handleClose: () => void;
  groupId: string;
  boundToSpaceGroup?: boolean;
  openn: boolean;
  classRoomId: string;
  userId: string;
  educatorOnly?: boolean;
  removeParticipant?: (participantId: string, groupId: string) => Promise<void>;
  // Optional: Pass conversation data directly to avoid re-fetching
  conversationData?: any;
}

const ConversationParticipantsListResponsiveContainer: React.FC<
  ConversationParticipantsListResponsiveContainerProps
> = ({
  handleClose,
  groupId,
  boundToSpaceGroup = false,
  openn,
  classRoomId,
  userId,
  educatorOnly = false,
  removeParticipant,
  conversationData,
}) => {
  // Always get fresh conversation data from Redux store to ensure we have the latest state
  const conversation = useSelector((state: any) =>
    conversationSelectors.conversation(state, groupId)
  );

  console.log('Current conversation from Redux:', conversation);

  const dispatch = useDispatch();

  // Get all users and live state from the store
  const allUsers = useSelector((state: any) => state.data.users);
  const liveState = useSelector((state: any) => state.live);

  // Get participants data
  const participants = useMemo(() => {
    console.log(
      'Participants useMemo triggered with conversation:',
      conversation
    );

    if (
      !conversation ||
      !conversation.participants ||
      typeof conversation.participants !== 'object'
    ) {
      console.log('No conversation or participants found');
      return [];
    }

    // Get all participant IDs from the conversation
    const participantIds = Object.keys(conversation.participants);
    console.log('Participant IDs from conversation:', participantIds);

    // Get user data for each participant
    const participantsData = participantIds
      .map((participantId) => {
        const userData = allUsers[participantId];
        if (!userData) {
          // If user data is not available, create a placeholder
          return {
            objectId: participantId,
            firstName: 'Loading...',
            lastName: '',
            avatar: undefined,
            online: false,
          };
        }
        return {
          objectId: userData.objectId || userData.id || participantId,
          firstName:
            userData.firstName || userData.name?.split(' ')[0] || 'Unknown',
          lastName:
            userData.lastName ||
            userData.name?.split(' ').slice(1).join(' ') ||
            '',
          avatar: userData.avatar || userData.profilePicture,
          online: liveState?.clients?.[participantId] || false,
        };
      })
      .sort((a, b) => {
        // Sort by first name, then last name
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

    return participantsData;
  }, [conversation, allUsers]);

  // Fetch users when conversation changes and participants are missing
  useEffect(() => {
    if (conversation && conversation.participants && openn) {
      const participantIds = Object.keys(conversation.participants);
      const missingUserIds = participantIds.filter(
        (participantId) => !allUsers[participantId]
      );

      if (missingUserIds.length > 0) {
        // Fetch missing users
        missingUserIds.forEach((userId) => {
          dispatch(fetchUser(userId));
        });
      }
    }
  }, [conversation, allUsers, openn, dispatch]);

  // Handle remove participant
  const handleRemoveParticipant = async (
    participantId: string,
    groupId: string
  ) => {
    try {
      console.log(
        'Starting remove participant:',
        participantId,
        'from:',
        groupId
      );
      console.log(
        'Current conversation participants:',
        conversation?.participants
      );

      // Check if removal is allowed based on conversation type and participant count
      const currentParticipantCount = Object.keys(
        conversation?.participants || {}
      ).length;

      if (boundToSpaceGroup) {
        throw new Error(
          'Cannot remove participants from space-bound conversations'
        );
      }

      if (educatorOnly) {
        throw new Error(
          "Participants of educator-only conversation can't be modified"
        );
      }

      if (currentParticipantCount <= 2) {
        throw new Error(
          'Cannot remove participant: conversation would become too small'
        );
      }

      // Prevent removing the current user (they shouldn't be in the list anyway)
      if (participantId === userId) {
        throw new Error('Cannot remove current user from conversation');
      }

      // Create a custom removeParticipant function that works with our context
      const customRemoveParticipant = async () => {
        const payload = { conversationId: groupId, participantId };

        console.log('Dispatching started action with payload:', payload);

        // Dispatch started action
        dispatch({
          type: INBOX_REMOVE_PARTICIPANT_FROM_CONVERSATION,
          status: 'started',
          payload,
        });

        try {
          console.log('Making API call to remove participant');
          // Make API call
          await api.conversations.removeParticipant(groupId, participantId);

          console.log('API call successful, dispatching success action');
          // Dispatch success action
          dispatch({
            type: INBOX_REMOVE_PARTICIPANT_FROM_CONVERSATION,
            status: 'success',
            payload,
          });

          console.log('Success action dispatched');
          return Promise.resolve();
        } catch (error) {
          console.error('API call failed:', error);
          // Dispatch error action
          dispatch({
            type: INBOX_REMOVE_PARTICIPANT_FROM_CONVERSATION,
            status: 'error',
            payload: { ...payload, error },
          });
          // Re-throw the error so the component can handle it
          throw error;
        }
      };

      // Use the custom function
      await customRemoveParticipant();
      console.log('Remove participant completed successfully');
    } catch (error) {
      console.error('Error removing participant:', error);
      // Re-throw the error so the component can show it to the user
      throw error;
    }
  };

  // Debug logging for props being passed
  console.log('Container props debug:', {
    boundToSpaceGroup,
    educatorOnly,
    participantsLength: participants.length,
    groupId,
    userId,
  });

  return (
    <ConversationParticipantsListResponsive
      handleClose={handleClose}
      groupId={groupId}
      boundToSpaceGroup={boundToSpaceGroup}
      openn={openn}
      participants={participants}
      classRoomId={classRoomId}
      userId={userId}
      educatorOnly={educatorOnly}
      removeParticipant={handleRemoveParticipant}
    />
  );
};

export default ConversationParticipantsListResponsiveContainer;
